import { useMemo } from 'react';
import { ICompatibilityResult, IRecipient } from '@nkr/models/index';
import { AgGridReact } from 'ag-grid-react';
import { ColGroupDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { Button, Label } from 'react-bootstrap';
import { GridWrapper } from './styles';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';
import ICompatibilityResultWithEpletMmRisk from './compatibility-result-with-eplet-mm-risk';

interface IProps {
    data: ICompatibilityResult[],
    placeHolderRecipient: IRecipient,
    onConfirm: (compatibilityResultsRecipient: IRecipient) => void,
}

const WaitListGrid = (props: IProps) => {
    const handleDonorEvaluation = (result: ICompatibilityResult) => {
        window.open('/center/' + result.recipient.centerId + '/recipient/' + result.recipient.id + '/evaluate-donor/' + result.donor.id, '_blank');
    }

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const colDefs: ColGroupDef[] = [
        {
            headerName: 'Available Waitlist Replacements',
            children: [
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Rank</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => <>{params.rowIndex + 1}</>,
                    headerName: 'Rank',
                    width: 50,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
                    headerName: 'Alias',
                    field: 'recipient.alias',
                    width: 115,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as ICompatibilityResultWithEpletMmRisk;
                        return (
                            <a href={'/center/' + data.recipient.centerId + '/recipient/' + data.recipient.id + '/profile'} target='_blank'>{data.recipient.alias}</a>
                        );
                    },
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Microsite<br />Status</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as ICompatibilityResultWithEpletMmRisk;
                        return data.recipient.micrositeActive ? <Label bsStyle='success'>Active</Label> : <Label bsStyle='default'>Inactive</Label>
                    },
                    headerName: 'Alias',
                    field: 'recipient.micrositeActive',
                    width: 80,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ABO</SortableHeaderComponent>,
                    headerName: 'ABO',
                    field: 'recipient.bloodType',
                    width: 40,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>cPRA</SortableHeaderComponent>,
                    headerName: 'cPRA',
                    field: 'recipient.cpra4',
                    width: 50,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>cPRA<br />Pts</SortableHeaderComponent>,
                    headerName: 'cPRA Pts',
                    field: 'waitlistAllocationPoints.cpraPoints',
                    width: 50,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>UNOS Wait<br />Months</SortableHeaderComponent>,
                    headerName: 'UNOS Wait Months',
                    field: 'recipient.unosWaitMonths',
                    width: 70,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>NKR Wait<br />Months</SortableHeaderComponent>,
                    headerName: 'NKR Wait Months',
                    field: 'recipient.nkrWaitMonths',
                    width: 70,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Wait<br />Pts</SortableHeaderComponent>,
                    headerName: 'Wait Pts',
                    field: 'waitlistAllocationPoints.waitPoints',
                    width: 50,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
                    headerName: 'Age',
                    field: 'recipient.age',
                    width: 40,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age<br />Pts</SortableHeaderComponent>,
                    headerName: 'Age Pts',
                    field: 'waitlistAllocationPoints.agePoints',
                    width: 40,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Eplet<br />MM</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as ICompatibilityResultWithEpletMmRisk;

                        return (
                            <SingleEpletMmRisk data={data.singleEpletMmRisk} isAdmin={false} />
                        );
                    },
                    headerName: 'Eplet MM',
                    width: 70,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Eplet MM<br />Pts</SortableHeaderComponent>,
                    field: 'waitlistAllocationPoints.epletMmPoints',
                    headerName: 'Eplet MM Pts',
                    width: 70,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Total<br />Pts</SortableHeaderComponent>,
                    headerName: 'Total Pts',
                    field: 'waitlistAllocationPoints.totalPoints',
                    width: 50,
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actions</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as ICompatibilityResultWithEpletMmRisk;

                        return (
                            <>
                                <Button bsStyle='primary' bsSize='xs' onClick={() => props.onConfirm(data.recipient)}>Select</Button>&nbsp;
                                <Button bsSize='xs' onClick={() => handleDonorEvaluation(data)}>Evaluate Donor</Button>
                            </>
                        )
                    },
                    headerClass: 'list-header-last',
                    headerName: 'Actions',
                    width: 160
                }
            ]
        }
    ];

    const columnDefs = useMemo(() => colDefs, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className='ag-theme-bootstrap' style={{ width: '100%', height: 400 }}>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true
                }}
                rowData={rowData}
                headerHeight={44}
                groupHeaderHeight={28}
                rowHeight={30}
                suppressCellSelection={true}
                suppressRowClickSelection={true}
                suppressContextMenu={true}
                enableCellTextSelection={true}
                alwaysShowVerticalScroll={true}
                immutableData={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onGridSizeChanged={onGridSizeChanged}
            />
        </GridWrapper>
    )
}

export default WaitListGrid;